<template>
  <v-dialog v-model="dialog" max-width="680" persistent scrollable>
    <v-card class="addon-dialog-card">
      <!-- Header -->
      <v-card-title class="dialog-header pa-0">
        <div class="header-inner">
          <div class="header-left">
            <div class="header-icon-wrap">
              <v-icon color="primary" size="20">mdi-puzzle-outline</v-icon>
            </div>
            <div>
              <div class="header-title">Program Add-ons</div>
              <div class="header-sub">
                Select add-ons to enhance your program
              </div>
            </div>
          </div>
          <v-btn icon small @click="closeDialog" class="close-btn">
            <v-icon size="18">mdi-close</v-icon>
          </v-btn>
        </div>

        <!-- Step indicator -->
        <div class="step-bar" v-if="!isReadOnly && !showPayments">
          <div v-for="(step, i) in confirmation_steps" :key="i" class="step-item"
            :class="{ active: currentStep === i, done: currentStep > i }">
            <div class="step-dot">
              <v-icon v-if="currentStep > i" size="12" color="white">mdi-check</v-icon>
              <span v-else>{{ i + 1 }}</span>
            </div>
            <span class="step-label">{{ step }}</span>
            <div v-if="i < steps.length - 1" class="step-line"></div>
          </div>
        </div>
        <div class="step-bar" v-if="!isReadOnly && showPayments">
          <div v-for="(step, i) in steps" :key="i" class="step-item"
            :class="{ active: currentStep === i, done: currentStep > i }">
            <div class="step-dot">
              <v-icon v-if="currentStep > i" size="12" color="white">mdi-check</v-icon>
              <span v-else>{{ i + 1 }}</span>
            </div>
            <span class="step-label">{{ step }}</span>
            <div v-if="i < steps.length - 1" class="step-line"></div>
          </div>
        </div>
      </v-card-title>

      <v-progress-linear v-if="loading" indeterminate color="primary" height="2" class="ma-0" />

      <!-- STEP 0: Add-on Selection -->
      <v-card-text class="dialog-body pa-0" v-if="currentStep === 0">
        <!-- Empty state -->
        <div v-if="!programAddons.length && !loading" class="empty-state">
          <v-icon size="40" color="grey lighten-1">mdi-puzzle-off-outline</v-icon>
          <div class="empty-title">No add-ons available</div>
          <div class="empty-sub">
            No program add-ons found for your selected program, batch, and year.
          </div>
        </div>

        <!-- Addon cards -->
        <div v-else class="addon-list">
          <div v-for="addon in programAddons" :key="addon.id" class="addon-card" :class="{
            'addon-selected': isSelected(addon.id),
            'addon-no-fee': !addon.feeSchedule,
            'addon-readonly': isReadOnly,
          }" @click="!isReadOnly && addon.feeSchedule && handleCardClick(addon.id)">
            <!-- Card main row -->
            <div class="addon-main">
              <div class="addon-left">
                <div class="addon-icon-wrap" :class="addon.type === 'Combo' ? 'icon-combo' : 'icon-single'">
                  <v-icon size="16" :color="addon.type === 'Combo' ? '#6d28d9' : 'primary'">
                    {{
                      addon.type === "Combo"
                        ? "mdi-puzzle-edit-outline"
                        : "mdi-puzzle-outline"
                    }}
                  </v-icon>
                </div>

                <div class="addon-info">
                  <div class="addon-name-row">
                    <span class="addon-name">{{ addon.name }}</span>
                    <v-chip v-if="addon.type != 'Single'" x-small
                      :color="addon.type === 'Combo' ? '#ede9fe' : '#eff6ff'" :text-color="addon.type === 'Combo' ? '#6d28d9' : '#1d4ed8'
                        " class="type-chip">
                      {{ addon.type }}
                    </v-chip>
                  </div>
                  <div v-if="expandedAddonId !== addon.id && (addon.type !== 'Combo')" class="addon-desc addon-description" style="display: -webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: 2; overflow: hidden; line-clamp: 2;">
                    <p v-html="addon.description"></p>
                  </div>
                  <div v-if="addon.type === 'Combo'" class="addon-desc">
                    <div class="combo-addons-list">
                      <span class="combo-label">Includes:</span>
                      <div class="combo-names">
                        <span v-for="(name, idx) in addon.combo_addon_names" :key="idx" class="combo-chip">
                          {{ name }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="addon-right">
                <div v-if="addon.feeSchedule" class="fee-info">
                  <div class="fee-amount">
                    {{ formatAmount(addon.feeSchedule.amount) }}
                  </div>
                  <div class="fee-deadline">
                    <v-icon size="10" color="grey">mdi-calendar-outline</v-icon>
                    {{ formatDate(addon.feeSchedule.enrollment_end) }}
                  </div>
                </div>
                <div v-else class="no-fee-badge">
                  <v-icon size="12" color="error">mdi-alert-circle-outline</v-icon>
                  <span>No fee</span>
                </div>

                <div v-if="isReadOnly" class="readonly-check">
                  <v-icon size="18" color="success">mdi-check-circle</v-icon>
                </div>
                <div v-else-if="addon.feeSchedule" class="addon-checkbox" :class="{ checked: isSelected(addon.id) }"
                  @click.stop="toggleSelect(addon.id)">
                  <v-icon v-if="isSelected(addon.id)" size="12" color="white">mdi-check</v-icon>
                </div>
                <div v-else class="addon-checkbox disabled">
                  <v-icon size="12" color="grey">mdi-minus</v-icon>
                </div>
              </div>
            </div>

            <!-- Expandable description -->
            <v-expand-transition>
              <div v-if="expandedAddonId === addon.id && addon.feeSchedule" class="addon-expanded">
                <div class="expanded-divider"></div>
                <div class="expanded-inner">
                  <div class="expanded-desc" v-html="addon.description"></div>

                </div>
              </div>
            </v-expand-transition>
          </div>
        </div>
      </v-card-text>

      <!-- STEP 1: Payment Option -->
      <v-card-text class="dialog-body pa-0" v-else-if="currentStep === 1">
        <div class="payment-step">
          <div class="payment-header">
            <div class="payment-header-title">Choose payment option</div>
          </div>

          <!-- Order summary -->
          <div class="order-summary">
            <div class="order-title">Programme Add-on summary</div>
            <div v-for="addon in selectedAddonDetails" :key="addon.id" class="order-item">
              <div class="order-item-left">
                <v-icon size="14" color="primary" class="mr-1">mdi-puzzle-outline</v-icon>
                <span class="order-item-name">{{ addon.name }}</span> &nbsp; <v-chip x-small
                  v-if="addon.type != 'Single'">{{ addon.type }}</v-chip>
              </div>
              <span class="order-item-price">{{
                formatAmount(addon.feeSchedule.amount)
              }}</span>
            </div>
            <div class="order-total-row">
              <span class="order-total-label">Total</span>
              <span class="order-total-amount">{{
                formatAmount(totalAmount)
              }}</span>
            </div>
          </div>

          <!-- Payment cards -->
          <div class="payment-cards" v-if="showPayments">
            <!-- Pay Now -->
            <div v-if="paymentEnabled" class="payment-card" :class="{ 'payment-card-selected': paymentOption === 'now' }"
              @click="paymentOption = 'now'">
              <div class="payment-card-inner">
                <div class="payment-radio" :class="{ active: paymentOption === 'now' }">
                  <div class="radio-dot" v-if="paymentOption === 'now'"></div>
                </div>
                <div class="payment-icon-wrap pay-now-icon">
                  <v-icon size="22" color="#0d7a4e">mdi-lightning-bolt</v-icon>
                </div>
                <div class="payment-content">
                  <div class="payment-title">Pay now</div>
                  <div class="payment-tag pay-now-tag">
                    <v-icon size="11">mdi-shield-check-outline</v-icon>
                    Instant payment
                  </div>
                </div>
                <div class="payment-amount">
                  <div class="pa-amount">{{ formatAmount(totalAmount) }}</div>
                </div>
              </div>
            </div>

            <!-- Pay Later -->
            <div v-if="showPayments" class="payment-card" :class="{ 'payment-card-selected': paymentOption === 'later' }"
              @click="paymentOption = 'later'">
              <div class="payment-card-inner">
                <div class="payment-radio" :class="{ active: paymentOption === 'later' }">
                  <div class="radio-dot" v-if="paymentOption === 'later'"></div>
                </div>
                <div class="payment-icon-wrap pay-later-icon">
                  <v-icon size="22" color="#92400e">mdi-calendar-clock</v-icon>
                </div>
                <div class="payment-content">
                  <div class="payment-title">Pay later</div>
                  <div class="payment-desc">
                    Reserve your spot now and complete payment before the
                    enrollment deadline.
                  </div>
                  <div class="payment-tag pay-later-tag">
                    <v-icon size="11">mdi-clock-outline</v-icon>
                    Reserve &amp; pay later
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Pay later note -->
          <v-expand-transition>
            <div v-if="paymentOption === 'later'" class="later-note">
              <v-icon size="14" color="#92400e" class="mr-1">mdi-information-outline</v-icon>
              Payment must be completed before the earliest enrollment deadline
              among your selected add-ons.
            </div>
          </v-expand-transition>
        </div>
      </v-card-text>

      <!-- Footer -->
      <v-card-actions class="dialog-footer pa-0" v-if="!isReadOnly && (programAddons.length || currentStep > 0)">
        <div class="footer-inner">
          <!-- Summary pill (step 0) -->
          <div class="footer-summary" v-if="currentStep === 0">
            <template v-if="selectedCount > 0">
              <v-icon size="14" color="success" class="mr-1">mdi-check-circle-outline</v-icon>
              <span class="summary-count">{{ selectedCount }} selected</span>
              <span class="summary-dot">·</span>
              <span class="summary-amount">{{
                formatAmount(totalAmount)
              }}</span>
            </template>
            <span v-else class="summary-empty">No add-ons selected</span>
          </div>

          <!-- Back label (step 1) -->
          <div class="footer-summary" v-else>
            <v-btn text small color="grey darken-1" @click="currentStep = 0" class="back-btn">
              <v-icon left size="16">mdi-arrow-left</v-icon>
              Back
            </v-btn>
          </div>

          <div class="footer-actions">
            <v-btn v-if="!showPayments" text color="grey darken-1" @click="skipDialog" class="btn-primary">Skip for now</v-btn>

            <!-- Step 0: Continue -->
            <v-btn v-if="currentStep === 0" color="primary" depressed :disabled="selectedCount === 0"
              @click="currentStep = 1" class="btn-primary">
              Continue
              <v-icon right size="16">mdi-arrow-right</v-icon>
            </v-btn>

            <v-btn v-if="!showPayments && currentStep === 1" color="primary" depressed :disabled="selectedCount === 0"
              @click="confirmAddons" class="btn-primary">
              Confirm
            </v-btn>

            <!-- Step 1: Submit -->
            <v-btn v-if="showPayments && currentStep === 1" :color="paymentOption === 'now' ? 'success' : 'primary'" depressed :disabled="!paymentOption"
              :loading="submitting" @click="submitAddons" class="btn-primary">
              <v-icon left size="16">
                {{
                  paymentOption === "now"
                    ? "mdi-lightning-bolt"
                    : "mdi-calendar-check-outline"
                }}
              </v-icon>
              {{ paymentOption === "now" ? "Pay now" : "Reserve & pay later" }}
            </v-btn>
          </div>
        </div>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import env from "@/helpers/env";
import { fetchPaymentEnabled } from "@/helpers/paymentAvailability";

export default {
  name: "ProgramAddonsDialog",

  filters: {
    stripHtml(value) {
      if (!value) return "";
      return value.replace(/<[^>]*>/g, "");
    },
  },

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    isReadOnly: {
      type: Boolean,
      default: false,
    },
    programId: {
      type: Number,
      default: null,
    },
    partnerId: {
      type: Number,
      default: null,
    },
    batch: {
      type: Number,
      default: null,
    },
    year: {
      type: Number,
      default: null,
    },
    existingProgramAddons: {
      type: Array,
      default: () => []
    },
    existingProgramAddonAmount: {
      type: [Number, String],
      default: null
    },
    showPayments: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      loading: false,
      submitting: false,
      env: env,
      programAddons: [],
      selectedAddons: {},
      currentStep: 0,
      paymentEnabled: false,
      paymentOption: "later",
      steps: ["Select add-ons", "Payment option"],
      confirmation_steps: ["Select add-ons", "Confirmation"],
      expandedAddonId: null,
    };
  },

  computed: {
    dialog: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },

    selectedCount() {
      return Object.values(this.selectedAddons).filter(Boolean).length;
    },

    selectedAddonDetails() {
      const selectedIds = Object.keys(this.selectedAddons)
        .filter((k) => this.selectedAddons[k])
        .map(Number);
      return this.programAddons.filter(
        (a) => selectedIds.includes(a.id) && a.feeSchedule,
      );
    },

    totalAmount() {
      return this.selectedAddonDetails.reduce(
        (sum, a) => sum + (parseInt(a.feeSchedule?.amount) || 0),
        0,
      );
    },
  },

  watch: {
    dialog(val) {
      if (val) {
        this.fetchFeeSchedules();
        this.loadPaymentAvailability();
        // Pre-select existing addons from applicant
        this.preSelectExistingAddons();
      } else {
        this.resetDialog();
      }
    },
  },

  methods: {
    async loadPaymentAvailability() {
      this.paymentEnabled = await fetchPaymentEnabled(this.$axios);
      if (!this.paymentEnabled) this.paymentOption = "later";
      else if (this.paymentOption === "later") this.paymentOption = "now";
    },

    isSelected(id) {
      return !!this.selectedAddons[id];
    },

    toggleSelect(id) {
      this.$set(this.selectedAddons, id, !this.selectedAddons[id]);
    },

    handleCardClick(id) {
      this.expandedAddonId =
        this.expandedAddonId === id ? null : id;
    },

    preSelectExistingAddons() {
      if (this.existingProgramAddons && this.existingProgramAddons.length > 0) {
        this.selectedAddons = {};
        this.existingProgramAddons.forEach(id => {
          this.$set(this.selectedAddons, id, true);
        });
      }
    },

    async fetchFeeSchedules() {
      this.loading = true;
      try {
        const params = {};
        if (this.programId) params.program_id = this.programId;
        if (this.partnerId) params.partner_id = this.partnerId;
        if (this.batch) params.batch = this.batch;
        if (this.year) params.year = this.year;

        const response = await this.$axios.get("/get_program_addons", {
          params,
        });
        const feeSchedules = response.data.data || [];
        this.processFeeSchedules(feeSchedules);
      } catch (error) {
        console.error("Error fetching fee schedules:", error);
        this.$store.commit("showSnackbar", {
          message:
            error.response?.data?.message || "Error fetching program add-ons",
          color: "error",
        });
      } finally {
        this.loading = false;
      }
    },

    async processFeeSchedules(feeSchedules) {
      const grouped = {};
      feeSchedules.forEach((fee) => {
        const id = fee.program_addon_id;
        if (!grouped[id]) grouped[id] = fee;
      });

      // First, collect all combo addons that need names
      const comboAddonsToFetch = [];
      const tempAddons = Object.keys(grouped).map((addonId) => {
        const fee = grouped[addonId];
        const addon = {
          id: fee.program_addon?.id || parseInt(addonId),
          name: fee.program_addon?.name || `Add-on ${addonId}`,
          description: fee.program_addon?.description || "",
          type: fee.program_addon?.type || "Single",
          combo_addons: fee.program_addon?.combo_addons || [],
          combo_addon_names: [],
          combo_addon_display: "",
          feeSchedule: fee.id
            ? {
              id: fee.id,
              amount: fee.amount,
              batch: fee.batch,
              year: fee.year,
              enrollment_end: fee.enrollment_end,
              program_id: fee.program_id,
              partner_id: fee.partner_id,
            }
            : null,
        };

        // Track combo addons that need names
        if (
          addon.type === "Combo" &&
          addon.combo_addons &&
          addon.combo_addons.length > 0
        ) {
          comboAddonsToFetch.push({
            id: addon.id,
            comboIds: addon.combo_addons,
          });
        }

        return addon;
      });

      // Fetch all combo names in parallel
      if (comboAddonsToFetch.length > 0) {
        const promises = comboAddonsToFetch.map(async (item) => {
          const names = await this.getComboNames(item.comboIds);
          return {
            id: item.id,
            names: names,
            display: names.map((n) => `+ ${n}`).join(" "),
          };
        });

        const comboResults = await Promise.all(promises);

        // Update addons with fetched combo names
        comboResults.forEach((result) => {
          const addon = tempAddons.find((a) => a.id === result.id);
          if (addon) {
            addon.combo_addon_names = result.names;
            addon.combo_addon_display = result.display;
          }
        });
      }

      this.programAddons = tempAddons.sort((a, b) =>
        a.name.localeCompare(b.name),
      );
    },

    formatAmount(amount) {
      if (!amount) return "₹0";
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount);
    },

    formatDate(date) {
      if (!date) return "-";
      return new Date(date).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },

    resetDialog() {
      this.selectedAddons = {};
      this.programAddons = [];
      this.currentStep = 0;
      this.paymentOption = this.paymentEnabled ? null : "later";
    },

    closeDialog() {
      this.dialog = false;
    },

    skipDialog() {
      this.$emit("skipAddons", {
        paymentOption: "skip"
      });
      this.dialog = false;
    },

    async confirmAddons() {
      this.paymentOption = "later";
      this.submitAddons();
    },

    async submitAddons() {
      this.submitting = true;
      try {
        const selectedIds = Object.keys(this.selectedAddons)
          .filter((k) => this.selectedAddons[k])
          .map(Number);

        this.$emit("submit", {
          ids: selectedIds,
          details: this.selectedAddonDetails,
          totalAmount: this.totalAmount,
          paymentOption: this.paymentOption, // 'now' | 'later'
        });

        this.closeDialog();
      } catch (error) {
        console.error("Error submitting add-ons:", error);
        this.$store.commit("showSnackbar", {
          message: "Error submitting add-ons",
          color: "error",
        });
      } finally {
        this.submitting = false;
      }
    },

    async getComboNames(comboIds) {
      if (!comboIds || comboIds.length === 0) return [];

      try {
        const response = await this.$axios.get(
          "/get_program_addons_combo_names",
          {
            params: { ids: comboIds.join(",") },
          },
        );

        return response.data.data.names;
      } catch (error) {
        console.error("Error fetching combo names:", error);
        return [];
      }
    },
  },
};
</script>

<style scoped>
/* ── Dialog card ── */
.addon-dialog-card {
  border-radius: 16px !important;
  overflow: hidden;
}

/* ── Header ── */
.dialog-header {
  display: block !important;
  padding: 0 !important;
  background: #fff;
  border-bottom: 1px solid #e8e8e8;
}

.header-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px 14px;
  gap: 12px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-icon-wrap {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: #eff6ff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.header-title {
  font-size: 15px;
  font-weight: 600;
  color: #1a1a1a;
  line-height: 1.3;
}

.header-sub {
  font-size: 12px;
  color: #888;
  margin-top: 1px;
}

.close-btn {
  border: 1px solid #e0e0e0 !important;
  background: #f9f9f9 !important;
}

/* ── Step bar ── */
.step-bar {
  display: flex;
  align-items: center;
  padding: 10px 20px;
  background: #fafafa;
  border-top: 1px solid #f0f0f0;
  gap: 0;
}

.step-item {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
}

.step-dot {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 1.5px solid #d0d0d0;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 600;
  color: #aaa;
  flex-shrink: 0;
  transition: all 0.2s;
}

.step-item.active .step-dot {
  border-color: #1976d2;
  background: #1976d2;
  color: #fff;
}

.step-item.done .step-dot {
  border-color: #43a047;
  background: #43a047;
  color: #fff;
}

.step-label {
  font-size: 12px;
  color: #999;
  font-weight: 500;
  transition: color 0.2s;
}

.step-item.active .step-label {
  color: #1976d2;
}

.step-item.done .step-label {
  color: #43a047;
}

.step-line {
  flex: 1;
  height: 1.5px;
  background: #e0e0e0;
  margin: 0 8px;
}

/* ── Body ── */
.dialog-body {
  overflow-y: auto !important;
  max-height: 420px;
}

/* ── Empty state ── */
.empty-state {
  text-align: center;
  padding: 48px 24px;
}

.empty-title {
  font-size: 15px;
  font-weight: 600;
  color: #555;
  margin-top: 12px;
}

.empty-sub {
  font-size: 13px;
  color: #999;
  margin-top: 4px;
  max-width: 320px;
  margin-left: auto;
  margin-right: auto;
}

/* ── Addon list ── */
.addon-list {
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* ── Addon card ── */
.addon-card {
  border: 1.5px solid #e8e8e8;
  border-radius: 12px;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.18s, box-shadow 0.18s, background 0.18s;
  overflow: hidden;
  border-left: 3px solid #1976d2;
}

.addon-card:hover:not(.addon-no-fee):not(.addon-readonly) {
  border-color: #bfdbfe;
  box-shadow: 0 2px 10px rgba(25, 118, 210, 0.08);
}

.addon-card.addon-selected {
  border-color: #bbf7d0;
  border-left-color: #16a34a;
  background: #f0fdf4;
  box-shadow: 0 2px 10px rgba(22, 163, 74, 0.07);
}

.addon-card.addon-no-fee {
  cursor: default;
  opacity: 0.7;
  border-left-color: #e53935;
}

.addon-card.addon-readonly {
  cursor: default;
}

/* ── Card main row ── */
.addon-main {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  gap: 12px;
}

.addon-left {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  flex: 1;
  min-width: 0;
}

.addon-icon-wrap {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 1px;
}

.icon-single {
  background: #eff6ff;
}

.icon-combo {
  background: #f5f3ff;
}

.addon-info {
  flex: 1;
  min-width: 0;
}

.addon-name-row {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.addon-name {
  font-size: 13px;
  font-weight: 600;
  color: #1a1a1a;
  line-height: 1.3;
}

.type-chip {
  font-size: 10px !important;
  height: 18px !important;
  font-weight: 600 !important;
}

.addon-desc {
  font-size: 12px;
  color: #777;
  margin-top: 3px;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.addon-description :deep(p)      { margin-bottom: 0.5rem; }
.addon-description :deep(ul)     { list-style: disc; padding-left: 1.25rem; margin-bottom: 0.5rem; }
.addon-description :deep(ol)     { list-style: decimal; padding-left: 1.25rem; margin-bottom: 0.5rem; }
.addon-description :deep(li)     { margin-bottom: 0.25rem; }
.addon-description :deep(strong) { font-weight: 600; color: #1a1a1a; }
.addon-description :deep(em)     { font-style: italic; }
.addon-description :deep(a)      { color: #2563eb; text-decoration: underline; }
.addon-description :deep(h1),
.addon-description :deep(h2),
.addon-description :deep(h3)     { font-weight: 700; margin-bottom: 0.4rem; color: #111; }
.addon-description :deep(h1)     { font-size: 1rem; }
.addon-description :deep(h2)     { font-size: 0.9rem; }
.addon-description :deep(h3)     { font-size: 0.85rem; }

/* ── Right side ── */
.addon-right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.fee-info {
  text-align: right;
}

.fee-amount {
  font-size: 14px;
  font-weight: 700;
  color: #1976d2;
}

.fee-deadline {
  font-size: 11px;
  color: #999;
  margin-top: 2px;
  display: flex;
  align-items: center;
  gap: 2px;
  justify-content: flex-end;
}

.no-fee-badge {
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 11px;
  color: #e53935;
  background: #fef2f2;
  padding: 3px 8px;
  border-radius: 20px;
  border: 1px solid #fecaca;
}

.addon-checkbox {
  width: 20px;
  height: 20px;
  border-radius: 6px;
  border: 1.5px solid #d0d0d0;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.18s;
  flex-shrink: 0;
}

.addon-checkbox.checked {
  background: #16a34a;
  border-color: #16a34a;
}

.addon-checkbox.disabled {
  background: #f5f5f5;
  border-color: #e0e0e0;
  cursor: not-allowed;
}

.readonly-check {
  display: flex;
  align-items: center;
}

/* ── Expanded section ── */
.expanded-divider {
  height: 1px;
  background: #e8f5e9;
  margin: 0 14px;
}

.expanded-inner {
  padding: 10px 14px 12px;
}

.expanded-desc {
  font-size: 12px;
  color: #555;
  line-height: 1.6;
}

.expanded-fees {
  display: flex;
  gap: 24px;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px dashed #c6f6d5;
}

.ef-item {
  flex: 1;
}

.ef-label {
  font-size: 11px;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 2px;
}

.ef-value {
  font-size: 13px;
  font-weight: 600;
  color: #1a1a1a;
}

/* ── Payment step ── */
.payment-step {
  padding: 16px;
}

.payment-header {
  margin-bottom: 14px;
}

.payment-header-title {
  font-size: 14px;
  font-weight: 600;
  color: #1a1a1a;
}

.payment-header-sub {
  font-size: 12px;
  color: #888;
  margin-top: 2px;
}

/* ── Order summary ── */
.order-summary {
  background: #f8faff;
  border: 1px solid #dbeafe;
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 14px;
}

.order-title {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #999;
  font-weight: 600;
  margin-bottom: 8px;
}

.order-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 5px 0;
  border-bottom: 1px dashed #e8f0fe;
}

.order-item:last-of-type {
  border-bottom: none;
}

.order-item-left {
  display: flex;
  align-items: center;
  min-width: 0;
}

.order-item-name {
  font-size: 13px;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 280px;
}

.order-item-price {
  font-size: 13px;
  font-weight: 600;
  color: #1976d2;
  flex-shrink: 0;
}

.order-total-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1.5px solid #bfdbfe;
}

.order-total-label {
  font-size: 13px;
  font-weight: 700;
  color: #1a1a1a;
}

.order-total-amount {
  font-size: 16px;
  font-weight: 700;
  color: #1976d2;
}

/* ── Payment cards ── */
.payment-cards {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.payment-card {
  border: 1.5px solid #e8e8e8;
  border-radius: 12px;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.18s, box-shadow 0.18s;
}

.payment-card:hover {
  border-color: #c0c0c0;
}

.payment-card-selected {
  border-color: #1976d2 !important;
  box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.08) !important;
}

.payment-card-inner {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px;
}

.payment-radio {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 1.5px solid #d0d0d0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 2px;
  transition: border-color 0.18s;
}

.payment-radio.active {
  border-color: #1976d2;
}

.radio-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #1976d2;
}

.payment-icon-wrap {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.pay-now-icon {
  background: #ecfdf5;
}

.pay-later-icon {
  background: #fffbeb;
}

.payment-content {
  flex: 1;
  min-width: 0;
}

.payment-title {
  font-size: 14px;
  font-weight: 700;
  color: #1a1a1a;
}

.payment-desc {
  font-size: 12px;
  color: #777;
  margin-top: 3px;
  line-height: 1.5;
}

.payment-tag {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 20px;
  margin-top: 7px;
}

.pay-now-tag {
  background: #ecfdf5;
  color: #15803d;
}

.pay-later-tag {
  background: #fffbeb;
  color: #92400e;
}

.payment-amount {
  text-align: right;
  flex-shrink: 0;
}

.pa-amount {
  font-size: 16px;
  font-weight: 700;
  color: #1a1a1a;
}

.pa-amount-muted {
  color: #bbb;
}

.pa-label {
  font-size: 11px;
  color: #aaa;
  margin-top: 1px;
}

/* ── Pay later note ── */
.later-note {
  margin-top: 10px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 12px;
  color: #92400e;
  line-height: 1.5;
  display: flex;
  align-items: flex-start;
}

/* ── Footer ── */
.dialog-footer {
  border-top: 1px solid #eeeeee !important;
  background: #fafafa !important;
}

.footer-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 12px 16px;
  gap: 12px;
}

.footer-summary {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #555;
  flex: 1;
}

.summary-count {
  font-weight: 600;
  color: #16a34a;
}

.summary-dot {
  color: #ccc;
}

.summary-amount {
  font-weight: 700;
  color: #1976d2;
}

.summary-empty {
  color: #bbb;
  font-size: 12px;
}

.footer-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.btn-cancel {
  font-size: 13px !important;
  font-weight: 500 !important;
}

.btn-primary {
  font-size: 13px !important;
  font-weight: 600 !important;
  border-radius: 8px !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
}

.back-btn {
  font-size: 13px !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
}

/* Combo addons styles */
.combo-addons-list {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
}

.combo-label {
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 12px;
}

.combo-names {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.combo-chip {
  background: #f5f3ff;
  color: #6d28d9;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
}

.combo-chip:before {
  margin-right: 3px;
  font-weight: bold;
}

/* ── Responsive ── */
@media (max-width: 480px) {
  .addon-main {
    flex-wrap: wrap;
  }

  .addon-right {
    width: 100%;
    justify-content: flex-end;
    padding-top: 6px;
    border-top: 1px dashed #eee;
  }

  .payment-card-inner {
    flex-wrap: wrap;
  }

  .payment-amount {
    width: 100%;
    text-align: left;
    margin-top: 6px;
    padding-top: 6px;
    border-top: 1px dashed #eee;
  }

  .order-item-name {
    max-width: 180px;
  }

  .footer-inner {
    flex-wrap: wrap;
    gap: 8px;
  }

  .footer-summary {
    width: 100%;
  }

  .footer-actions {
    width: 100%;
    justify-content: flex-end;
  }
}
</style>
